/**
 * InstallAppDialog — authenticated staff-only install experience.
 *
 * Wired from My Guru → Profile & Preferences → "Install Gleam Guru App" tile.
 * Platform-aware:
 *   • If the app is already installed (display-mode:standalone or
 *     navigator.standalone) → show the "Installed" confirmation and
 *     suppress installation prompts.
 *   • If `beforeinstallprompt` was captured (Chrome/Edge/Android) → offer
 *     the native install button that fires the browser's own dialog.
 *   • If the browser is iOS Safari (no programmatic install API) → show
 *     the Share → Add to Home Screen instruction sheet.
 *   • Everything else (desktop Safari, Firefox mobile, etc.) → generic
 *     browser-menu fallback instructions.
 *
 * DO NOT expose this outside AdminShell — this is staff-only.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { CheckCircle2, Download, Share2, Plus, Smartphone } from 'lucide-react';

const InstallAppDialog = ({ open, onClose }) => {
  const { canPrompt, installed, prompt, isIOS } = usePwaInstall();

  const handleInstall = async () => {
    const result = await prompt();
    if (result?.outcome === 'accepted') onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="install-app-dialog">
        {/* Guru icon header */}
        <div className="bg-[#F56F05] px-6 pt-6 pb-5 text-center">
          <img
            src="/icons/guru-192.png?v=3"
            alt="Gleam Guru app icon"
            className="w-20 h-20 mx-auto rounded-2xl shadow-lg ring-2 ring-white/30"
            data-testid="install-app-icon"
          />
          <p className="mt-3 text-white font-black text-lg tracking-tight">Gleam Guru</p>
          <p className="text-[11px] uppercase tracking-widest text-white/80 font-bold">Staff App</p>
        </div>

        <div className="px-6 py-5">
          <DialogHeader className="text-left space-y-1">
            <DialogTitle className="text-lg" data-testid="install-app-title">
              {installed ? 'App installed' : 'Install Gleam Guru'}
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              {installed
                ? 'Gleam Guru is running from your Home Screen. You\u2019re all set.'
                : 'Add Gleam Guru to your Home Screen for faster access and notifications.'}
            </DialogDescription>
          </DialogHeader>

          {/* Body varies by platform */}
          {installed ? (
            <div
              className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800"
              data-testid="install-app-installed-state"
            >
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <span className="font-semibold">Gleam Guru App Installed</span>
            </div>
          ) : canPrompt ? (
            <div className="mt-4 space-y-3" data-testid="install-app-native-block">
              <p className="text-[13px] text-zinc-600">
                One tap and Gleam Guru will live on your Home Screen — just like a native app.
              </p>
              <Button
                onClick={handleInstall}
                className="w-full bg-[#F56F05] hover:bg-orange-600 h-10"
                data-testid="install-app-native-btn"
              >
                <Download className="w-4 h-4 mr-1.5" /> Install Now
              </Button>
            </div>
          ) : isIOS ? (
            <div className="mt-4 space-y-3" data-testid="install-app-ios-block">
              <ol className="space-y-2.5 text-[13px] text-zinc-700">
                <Step index={1}>
                  Tap the <span className="inline-flex items-center gap-1 font-semibold text-zinc-900"><Share2 className="w-3.5 h-3.5" /> Share</span> button in Safari.
                </Step>
                <Step index={2}>
                  Select <span className="inline-flex items-center gap-1 font-semibold text-zinc-900"><Plus className="w-3.5 h-3.5" /> Add to Home Screen</span>.
                </Step>
                <Step index={3}>
                  Confirm <span className="font-semibold text-zinc-900">Add</span>.
                </Step>
              </ol>
              <div className="rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-[12px] text-zinc-600 flex items-start gap-2">
                <Smartphone className="w-3.5 h-3.5 mt-0.5 text-[#F56F05] flex-shrink-0" />
                <span>Gleam Guru will then launch from your Home Screen like a native app.</span>
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-3" data-testid="install-app-fallback-block">
              <p className="text-[13px] text-zinc-600">
                Open your browser&apos;s menu and look for one of these options:
              </p>
              <ul className="space-y-2 text-[13px] text-zinc-700 list-disc pl-5">
                <li><span className="font-semibold text-zinc-900">Install Gleam Guru</span> (Chrome, Edge, Brave)</li>
                <li><span className="font-semibold text-zinc-900">Add to Home Screen</span> (Firefox mobile, Samsung Internet)</li>
                <li><span className="font-semibold text-zinc-900">Add to Dock</span> (Safari on macOS)</li>
              </ul>
              <p className="text-[12px] text-zinc-500 italic">
                If none of these appear, your browser may not yet support installable apps.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 pb-5 pt-0">
          <Button variant="outline" onClick={onClose} className="w-full" data-testid="install-app-close-btn">
            {installed ? 'Done' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Step = ({ index, children }) => (
  <li className="flex gap-3">
    <span className="w-6 h-6 rounded-full bg-[#F56F05] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">
      {index}
    </span>
    <span className="flex-1 pt-0.5 leading-relaxed">{children}</span>
  </li>
);

export default InstallAppDialog;
